// ============================================================================
// VINStep.jsx - Enhanced with VIN Barcode Scanning
// ============================================================================
// Features:
// - Manual VIN entry (desktop/mobile)
// - Camera barcode scanning (mobile only)
// - Uses quagga.js for barcode detection
// - Supports Code 39 and Code 128 (standard VIN barcode formats)
// ============================================================================

import React, { Component } from 'react';
import { HTTPService } from "./../../../services/httpService";
import VINScanner from './VINScanner'; // New component we'll create

const API = new HTTPService();

class VINStep extends Component {
  constructor(props) {
    super(props);

    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Check if VIN is system-generated (starts with "NV")
    const isSystemVIN = props.vin && props.vin.toUpperCase().startsWith('NV');

    this.state = {
      vin: isSystemVIN ? '' : (props.vin || ''), // Don't show system-generated VINs
      vinConfirmed: false,
      loading: false,
      error: null,

      // Scanner state
      isMobile: isMobile,
      showScanner: false,
      scannerAvailable: false
    };

    this.handleVINChange = this.handleVINChange.bind(this);
    this.handleVerifyVIN = this.handleVerifyVIN.bind(this);
    this.handleVINScanned = this.handleVINScanned.bind(this);
  }

  componentDidMount() {
    // Check if camera/scanner is available
    this.checkScannerAvailability();
  }

  checkScannerAvailability = async () => {
    try {
      // Check if getUserMedia is supported
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Try to get camera permissions (doesn't actually start camera)
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // Immediately stop
        
        this.setState({ scannerAvailable: true });
      }
    } catch (error) {
      console.log('Camera not available:', error);
      this.setState({ scannerAvailable: false });
    }
  };

  handleVINChange(event) {
    const vin = event.target.value.toUpperCase();
    this.setState({ 
      vin: vin,
      error: null 
    });
  }

  async handleVINScanned(scannedVIN) {
    console.log("📸 VIN scanned:", scannedVIN);

    this.setState({
      vin: scannedVIN.toUpperCase(),
      showScanner: false,
      error: null,
      loading: true
    });

    // Automatically verify the scanned VIN
    try {
      const vin = scannedVIN.toUpperCase();

      console.log("🔍 Auto-verifying scanned VIN:", vin);

      // Call backend to verify VIN
      const { data } = await API.post(
        `/requests/${this.props.requestId}/checkin/vin`,
        {
          vin: vin,
          vin_confirmed: true
        }
      );

      if (!data.success) {
        throw new Error(data.error || 'Failed to verify VIN');
      }

      console.log("✅ VIN verified successfully");

      this.setState({
        vinConfirmed: true,
        loading: false
      });

      // Notify parent component to proceed to next step
      this.props.onVINVerified(vin);

    } catch (error) {
      console.error("❌ Error verifying VIN:", error);
      this.setState({
        loading: false,
        error: error.message
      });
    }
  }

  openScanner = () => {
    this.setState({ showScanner: true });
  };

  closeScanner = () => {
    this.setState({ showScanner: false });
  };

  async handleVerifyVIN() {
    try {
      const { vin } = this.state;

      // Validate VIN length
      if (vin.length !== 17) {
        this.setState({ error: 'VIN must be exactly 17 characters' });
        return;
      }

      // Basic VIN validation (alphanumeric, no I, O, Q)
      const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
      if (!vinPattern.test(vin)) {
        this.setState({ error: 'Invalid VIN format. VIN cannot contain I, O, or Q.' });
        return;
      }

      this.setState({ loading: true, error: null });

      console.log("🔍 Verifying VIN:", vin);

      // Call backend to verify VIN
      const { data } = await API.post(
        `/requests/${this.props.requestId}/checkin/vin`,
        {
          vin: vin,
          vin_confirmed: true
        }
      );

      if (!data.success) {
        throw new Error(data.error || 'Failed to verify VIN');
      }

      console.log("✅ VIN verified successfully");

      this.setState({ 
        vinConfirmed: true,
        loading: false 
      });

      // Notify parent component
      this.props.onVINVerified(vin);

    } catch (error) {
      console.error("❌ Error verifying VIN:", error);
      this.setState({ 
        loading: false,
        error: error.message 
      });
    }
  }

  render() {
    // Show scanner fullscreen
    if (this.state.showScanner) {
      return (
        <VINScanner
          onVINScanned={this.handleVINScanned}
          onClose={this.closeScanner}
        />
      );
    }

    return (
      <div className="vin-step">
        <h4 className="text-center mb-4">Step 1: Verify VIN</h4>

        <div className="row justify-content-center">
          <div className="col-md-8">

            {/* Scanner Button (Mobile Only) */}
            {this.state.isMobile && this.state.scannerAvailable && !this.state.vinConfirmed && (
              <div className="text-center mb-3">
                <button
                  className="btn btn-outline-primary btn-lg btn-block"
                  onClick={this.openScanner}
                  disabled={this.state.loading}
                >
                  <i className="fa fa-camera mr-2"></i>
                  Scan with Camera
                </button>
              </div>
            )}

            {/* Divider */}
            {this.state.isMobile && this.state.scannerAvailable && !this.state.vinConfirmed && (
              <div className="text-center my-3">
                <small className="text-muted">OR</small>
              </div>
            )}

            {/* VIN Input */}
            <div className="form-group">
              <label htmlFor="vin-input" className="font-weight-bold">
                17-Character Vehicle ID
              </label>
              <input
                id="vin-input"
                type="text"
                className="form-control form-control-lg text-center"
                value={this.state.vin}
                onChange={this.handleVINChange}
                placeholder="Enter vehicle identification number"
                maxLength={17}
                disabled={this.state.loading || this.state.vinConfirmed}
                style={{
                  letterSpacing: '2px',
                  fontFamily: 'monospace',
                  fontSize: '1.2rem'
                }}
              />
            </div>

            {/* Error Display */}
            {this.state.error && (
              <div className="alert alert-danger" role="alert">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                {this.state.error}
              </div>
            )}

            {/* Success Display */}
            {this.state.vinConfirmed && (
              <div className="alert alert-success" role="alert">
                <i className="fa fa-check-circle mr-2"></i>
                Verified successfully! Click NEXT to continue.
              </div>
            )}

            {/* Verify Button */}
            {!this.state.vinConfirmed && (
              <div className="text-center mt-4">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={this.handleVerifyVIN}
                  disabled={this.state.vin.length !== 17 || this.state.loading}
                >
                  {this.state.loading && (
                    <span className="mr-2">
                      <i className="fa fa-refresh fa-spin" />
                    </span>
                  )}
                  Verify & Continue
                </button>
              </div>
            )}

            {/* Help Text */}
            <div className="card bg-light mt-4">
              <div className="card-body">
                <h6 className="card-title">
                  <i className="fa fa-info-circle text-primary mr-2"></i>
                  Where to find it:
                </h6>
                <ul className="mb-0 small">
                  <li>Driver's side dashboard (visible through windshield)</li>
                  <li>Driver's side door jamb sticker</li>
                  <li>Vehicle registration or insurance documents</li>
                  <li>Engine block (stamped)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default VINStep;